import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
} from "@mui/icons-material";
import { DataGrid } from "@mui/x-data-grid";
import api from "../utils/api";
import AiSystemForm from "../components/AiSystemForm";
import {
  APPROVAL_STATUS_OPTIONS,
  LIFECYCLE_STAGES,
  RECORD_STATUS_OPTIONS,
} from "../constants/aiSystemsOptions";
import { formatLastUpdatedBy } from "../utils/aiSystemActivity";
import { formatUkDate } from "../utils/dateLocale";

const AiSystems = () => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [openForm, setOpenForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [total, setTotal] = useState(0);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 25,
  });
  const [filters, setFilters] = useState({
    search: "",
    lifecycleStage: "",
    approvalStatus: "",
    recordStatus: "",
    clientId: "",
  });
  const [clients, setClients] = useState([]);

  const fetchRows = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        page: paginationModel.page + 1,
        pageSize: paginationModel.pageSize,
      };
      if (filters.search) params.search = filters.search;
      if (filters.lifecycleStage)
        params.lifecycleStage = filters.lifecycleStage;
      if (filters.approvalStatus)
        params.approvalStatus = filters.approvalStatus;
      if (filters.recordStatus) params.recordStatus = filters.recordStatus;
      if (filters.clientId) params.clientId = filters.clientId;

      const response = await api.get("/api/ai-systems", { params });
      setRows(response.data.aiSystems || []);
      setTotal(response.data.total || 0);
      setError("");
    } catch (err) {
      console.error("Failed to fetch AI systems:", err);
      setError(err.response?.data?.error || "Failed to load AI systems");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [filters, paginationModel]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  useEffect(() => {
    api
      .get("/api/clients/list")
      .then((res) => setClients(res.data.clients || []))
      .catch(() => setClients([]));
  }, []);

  const handleDelete = async (row) => {
    if (!window.confirm(`Delete ${row.systemCode} – ${row.name}?`)) return;
    try {
      await api.delete(`/api/ai-systems/${row.id}`);
      fetchRows();
    } catch (err) {
      setError(err.response?.data?.error || "Failed to delete AI system");
    }
  };

  const handleFormSubmit = async (payload) => {
    if (editing) {
      await api.put(`/api/ai-systems/${editing.id}`, payload);
    } else {
      await api.post("/api/ai-systems", payload);
    }
    setOpenForm(false);
    setEditing(null);
    fetchRows();
  };

  const columns = [
    { field: "systemCode", headerName: "ID", width: 100, minWidth: 90 },
    {
      field: "name",
      headerName: "AI System / Tool",
      flex: 1.5,
      minWidth: 180,
    },
    {
      field: "providerName",
      headerName: "Provider",
      flex: 1,
      minWidth: 140,
    },
    { field: "clientName", headerName: "Client", flex: 1, minWidth: 140 },
    {
      field: "recordStatus",
      headerName: "Record",
      width: 110,
      minWidth: 100,
      renderCell: (params) => (
        <Chip
          size="small"
          label={params.value || "Active"}
          color={params.value === "Draft" ? "warning" : "success"}
        />
      ),
    },
    {
      field: "lifecycleStage",
      headerName: "Lifecycle",
      flex: 0.8,
      minWidth: 120,
    },
    {
      field: "approvalStatus",
      headerName: "Status",
      flex: 1,
      minWidth: 140,
    },
    { field: "ownerName", headerName: "Owner", flex: 1, minWidth: 130 },
    {
      field: "lastUpdatedByName",
      headerName: "Last updated by",
      flex: 1,
      minWidth: 140,
      valueGetter: (_value, row) =>
        formatLastUpdatedBy(row?.lastUpdatedByName, row?.updatedAt),
    },
    {
      field: "reviewDate",
      headerName: "Review date",
      width: 130,
      minWidth: 120,
      valueFormatter: (value) => (value ? formatUkDate(value) : ""),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 100,
      minWidth: 100,
      sortable: false,
      renderCell: (params) => (
        <Box display="flex" gap={0.5}>
          <IconButton
            size="small"
            color="primary"
            onClick={() => {
              setEditing(params.row);
              setOpenForm(true);
            }}
            aria-label="Edit"
          >
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            color="error"
            onClick={() => handleDelete(params.row)}
            aria-label="Delete"
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <Box width="100%">
      <Box display="flex" justifyContent="flex-end" alignItems="center" mb={3}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => {
            setEditing(null);
            setOpenForm(true);
          }}
        >
          Add AI System
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      <Card sx={{ mb: 2, width: "100%" }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Search"
                value={filters.search}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, search: e.target.value }))
                }
                placeholder="Name, ID, owner, use case"
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Lifecycle</InputLabel>
                <Select
                  label="Lifecycle"
                  value={filters.lifecycleStage}
                  onChange={(e) =>
                    setFilters((prev) => ({
                      ...prev,
                      lifecycleStage: e.target.value,
                    }))
                  }
                >
                  <MenuItem value="">All</MenuItem>
                  {LIFECYCLE_STAGES.map((o) => (
                    <MenuItem key={o} value={o}>
                      {o}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Record</InputLabel>
                <Select
                  label="Record"
                  value={filters.recordStatus}
                  onChange={(e) =>
                    setFilters((prev) => ({
                      ...prev,
                      recordStatus: e.target.value,
                    }))
                  }
                >
                  <MenuItem value="">All</MenuItem>
                  {RECORD_STATUS_OPTIONS.map((o) => (
                    <MenuItem key={o} value={o}>
                      {o}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  label="Status"
                  value={filters.approvalStatus}
                  onChange={(e) =>
                    setFilters((prev) => ({
                      ...prev,
                      approvalStatus: e.target.value,
                    }))
                  }
                >
                  <MenuItem value="">All</MenuItem>
                  {APPROVAL_STATUS_OPTIONS.map((o) => (
                    <MenuItem key={o} value={o}>
                      {o}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Client</InputLabel>
                <Select
                  label="Client"
                  value={filters.clientId}
                  onChange={(e) =>
                    setFilters((prev) => ({
                      ...prev,
                      clientId: e.target.value,
                    }))
                  }
                >
                  <MenuItem value="">All</MenuItem>
                  {clients.map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      {c.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card sx={{ width: "100%" }}>
        <CardContent sx={{ width: "100%", "&:last-child": { pb: 2 } }}>
          <Box height="calc(100vh - 300px)" minHeight={420} width="100%">
            <DataGrid
              rows={rows}
              columns={columns}
              loading={loading}
              getRowId={(row) => row.id}
              rowCount={total}
              paginationMode="server"
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              pageSizeOptions={[10, 25, 50]}
              disableSelectionOnClick
              disableColumnMenu
              sx={{
                width: "100%",
                height: "100%",
                border: 0,
                "& .MuiDataGrid-main": { width: "100%" },
                "& .MuiDataGrid-virtualScroller": { width: "100%" },
              }}
            />
          </Box>
        </CardContent>
      </Card>

      <Dialog
        open={openForm}
        onClose={() => {
          setOpenForm(false);
          setEditing(null);
        }}
        maxWidth="lg"
        fullWidth
        fullScreen={fullScreen}
        PaperProps={{
          sx: {
            height: { xs: "100%", md: "90vh" },
            maxHeight: "90vh",
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        <DialogTitle>
          {editing
            ? `Edit ${editing.systemCode || "AI System"}`
            : "Add AI System"}
        </DialogTitle>
        <DialogContent
          sx={{
            flex: 1,
            minHeight: 0,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <AiSystemForm
            aiSystem={editing}
            onSubmit={handleFormSubmit}
            onCancel={() => {
              setOpenForm(false);
              setEditing(null);
            }}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default AiSystems;
